import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Layout,
  Card,
  Typography,
  Button,
  Row,
  Col,
  Descriptions,
  Tag,
  Progress,
  Tabs,
  Table,
  Modal,
  Form,
  Input,
  message,
  Space,
  Statistic,
  Alert,
  Divider,
  Tooltip,
  Popconfirm,
  Badge,
  DatePicker,
  Select,
  Checkbox,
  Image,
  Drawer,
  Timeline,
  Empty,
  Pagination
} from 'antd';
import {
  ArrowLeftOutlined,
  KeyOutlined,
  BarChartOutlined,
  CodeOutlined,
  SettingOutlined,
  PlusOutlined,
  DeleteOutlined,
  ReloadOutlined,
  CopyOutlined,
  ApiOutlined,
  EyeOutlined,
  HistoryOutlined,
  SearchOutlined,
  FilterOutlined,
  DownloadOutlined,
  ZoomInOutlined,
  FileImageOutlined,
  CalendarOutlined,
  ExportOutlined
} from '@ant-design/icons';
import { applicationAPI, appOcrAPI, documentTypesAPI } from '../../services/api';
import ApplicationAnalytics from '../../components/ApplicationAnalytics';
import LoadingAnimation from '../../components/LoadingAnimation';

const { Content } = Layout;
const { Title, Text, Paragraph } = Typography;
const { TabPane } = Tabs;
const { TextArea } = Input;
const { RangePicker } = DatePicker;
const { Option } = Select;

interface Application {
  id: string;
  name: string;
  description: string;
  status: 'active' | 'inactive' | 'suspended';
  quota_limit: number;
  quota_used: number;
  current_usage: number;
  rate_limit_per_minute: number;
  total_requests: number;
  created_at: string;
  quota_info: {
    available: boolean;
    quota_remaining: number;
    quota_used: number;
    quota_limit: number;
  };
  api_keys?: APIKey[];
}

interface APIKey {
  id: string;
  name: string;
  description: string;
  status: 'active' | 'inactive' | 'revoked';
  created_at: string;
  last_used_at?: string;
  total_requests: number;
}

interface IntegrationGuide {
  application: Application;
  api_endpoints: {
    base_url: string;
    endpoints: Array<{
      name: string;
      method: string;
      path: string;
      description: string;
      headers?: Record<string, string>;
      body?: Record<string, any>;
      query_params?: Record<string, string>;
      features?: string[];
    }>;
  };
  code_examples: {
    curl?: string;
    python?: string;
    javascript?: string;
    node_js?: string;
  };
  best_practices?: {
    performance: string[];
    integration: string[];
    optimization: string[];
  };
  response_format?: {
    success_response: any;
    error_response: any;
  };
  active_api_keys: number;
  rate_limits: {
    requests_per_minute: number;
    quota_limit: number;
    quota_used: number;
  };
}

interface OCRRequest {
  id: string;
  client_id: string;
  application_id: string;
  api_key_id: string;
  document_type?: string;
  schema_version?: string;
  status: 'processing' | 'completed' | 'failed';
  processing_time_ms: number;
  request_size_bytes: number;
  image_url?: string;
  extracted_data: any;
  raw_text?: string;
  confidence_score: number;
  error_message?: string;
  created_at: string;
  updated_at: string;
  metadata?: {
    user_agent?: string;
    ip_address?: string;
    file_name?: string;
    file_size?: number;
    mime_type?: string;
  };
}

interface LogsFilter {
  search?: string;
  status?: string[];
  document_type?: string[];
  date_range?: [string, string];
  api_key_id?: string;
  min_confidence?: number;
}

interface PaginationInfo {
  current: number;
  pageSize: number;
  total: number;
}

const ApplicationDetail: React.FC = () => {
  const { appId } = useParams<{ appId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [application, setApplication] = useState<Application | null>(null);
  const [integrationGuide, setIntegrationGuide] = useState<IntegrationGuide | null>(null);
  const [loading, setLoading] = useState(true);
  
  // Get initial tab from URL parameter
  const urlParams = new URLSearchParams(location.search);
  const initialTab = urlParams.get('tab') || 'overview';
  const [activeTab, setActiveTab] = useState(initialTab);
  
  // Modal states
  const [createKeyModal, setCreateKeyModal] = useState(false);
  const [settingsModal, setSettingsModal] = useState(false);
  const [creatingApiKey, setCreatingApiKey] = useState(false);
  
  // Logs state
  const [ocrRequests, setOcrRequests] = useState<OCRRequest[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsFilter, setLogsFilter] = useState<LogsFilter>({});
  const [availableDocumentTypes, setAvailableDocumentTypes] = useState<{value: string, label: string}[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo>({
    current: 1,
    pageSize: 20,
    total: 0
  });
  const skipNextEffect = useRef(false); // Use ref to skip next effect trigger
  const loadingRef = useRef({ applicationData: false, documentTypes: false, logs: false });
  
  // Log details modal
  const [selectedRequest, setSelectedRequest] = useState<OCRRequest | null>(null);
  const [requestDetailsModal, setRequestDetailsModal] = useState(false);
  const [imagePreviewModal, setImagePreviewModal] = useState(false);
  const [selectedLogIds, setSelectedLogIds] = useState<string[]>([]);
  const [newApiKey, setNewApiKey] = useState<string>('');
  
  const [form] = Form.useForm();
  const [settingsForm] = Form.useForm();

  useEffect(() => {
    if (appId) {
      loadApplicationData();
    }
  }, [appId]);

  useEffect(() => {
    // Skip if this effect was triggered by setPagination from API response
    if (skipNextEffect.current) {
      skipNextEffect.current = false;
      return;
    }
    
    if (appId && activeTab === 'logs') {
      const timer = setTimeout(() => {
        loadLogs();
      }, 300); // Debounce
      
      // Always load document types when accessing logs tab to ensure they're current
      if (application) {
        loadDocumentTypes(application);
      }
      
      return () => clearTimeout(timer);
    }
  }, [appId, activeTab, pagination.current, pagination.pageSize, logsFilter.status, logsFilter.document_type, logsFilter.date_range]);

  // Load document types when switching to logs tab and we don't have types yet
  useEffect(() => {
    if (activeTab === 'logs' && application && availableDocumentTypes.length === 0) {
      loadDocumentTypes(application);
    }
  }, [activeTab, application]);

  const loadApplicationData = async () => {
    if (loadingRef.current.applicationData) return;
    
    try {
      loadingRef.current.applicationData = true;
      setLoading(true);
      const [appResponse, guideResponse] = await Promise.all([
        applicationAPI.getApplication(appId!),
        applicationAPI.getIntegrationGuide(appId!)
      ]);
      
      setApplication(appResponse.data.data);
      setIntegrationGuide(guideResponse.data.data);
      
      // Load document types for the application
      await loadDocumentTypes(appResponse.data.data);
    } catch (error) {
      console.error('Error loading application:', error);
      message.error('Failed to load application data');
    } finally {
      setLoading(false);
      loadingRef.current.applicationData = false;
    }
  };

  const loadDocumentTypes = async (app: Application) => {
    if (loadingRef.current.documentTypes) return;
    
    try {
      loadingRef.current.documentTypes = true;
      console.log('Loading document types for application:', app.id);
      
      // Use the proper backend proxy endpoint
      try {
        const response = await documentTypesAPI.getDocumentTypes(true);
        console.log('Document types API response:', response.data);
        
        if (response.data.success && response.data.data && response.data.data.length > 0) {
          const docTypes = response.data.data.map((dt: any) => ({
            value: dt.document_type,
            label: dt.display_name || dt.document_type
          }));
          setAvailableDocumentTypes(docTypes);
          console.log('Successfully loaded document types from API:', docTypes);
          return;
        }
      } catch (apiError) {
        console.error('Document types API failed:', apiError);
      }

      // Method 2: Fallback - extract document types from current logs data
      if (ocrRequests.length > 0) {
        const uniqueDocTypes = Array.from(new Set(
          ocrRequests
            .map(req => req.document_type)
            .filter(dt => dt && dt !== '')
        )).map(dt => {
          // Create proper labels for common document types
          const labelMap: Record<string, string> = {
            'driverslicense': "Driver's License",
            'passport': 'Passport',
            'invoice': 'Invoice',
            'receipt': 'Receipt',
            'generaldocument': 'General Document',
            'aadhaar': 'Aadhaar',
            'aadhar': 'Aadhaar', // Alternative spelling
          };
          
          return {
            value: dt!, // Keep the exact value from database
            label: labelMap[dt!.toLowerCase()] || dt!.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, str => str.toUpperCase())
          };
        });
        
        if (uniqueDocTypes.length > 0) {
          setAvailableDocumentTypes(uniqueDocTypes);
          console.log('Using document types from logs data:', uniqueDocTypes);
          return;
        }
      }

      // Method 3: Final fallback - use common document types
      const fallbackTypes = [
        { value: 'driverslicense', label: "Driver's License" },
        { value: 'passport', label: 'Passport' },
        { value: 'invoice', label: 'Invoice' },
        { value: 'receipt', label: 'Receipt' },
        { value: 'generaldocument', label: 'General Document' },
        { value: 'aadhaar', label: 'Aadhaar' }
      ];
      
      setAvailableDocumentTypes(fallbackTypes);
      console.log('Using fallback document types:', fallbackTypes);
      
    } catch (error) {
      console.error('Error loading document types:', error);
      setAvailableDocumentTypes([]);
    } finally {
      loadingRef.current.documentTypes = false;
    }
  };

  const handleCreateApiKey = async (values: { name: string; description: string }) => {
    try {
      setCreatingApiKey(true);
      const response = await applicationAPI.createApiKey(appId!, values);
      const { key_info, api_key } = response.data.data;
      
      setNewApiKey(api_key);
      message.success('API key created successfully');
      
      // Reload application data to show new key
      await loadApplicationData();
      
      form.resetFields();
    } catch (error) {
      console.error('Error creating API key:', error);
      message.error('Failed to create API key');
    } finally {
      setCreatingApiKey(false);
    }
  };

  const handleRevokeApiKey = async (keyId: string) => {
    try {
      await applicationAPI.revokeApiKey(keyId);
      message.success('API key revoked successfully');
      await loadApplicationData();
    } catch (error) {
      console.error('Error revoking API key:', error);
      message.error('Failed to revoke API key');
    }
  };

  const handleOpenSettings = () => {
    // Pre-populate the form with current application data
    settingsForm.setFieldsValue({
      name: application?.name,
      description: application?.description,
      quota_limit: application?.quota_limit,
      rate_limit_per_minute: application?.rate_limit_per_minute
    });
    setSettingsModal(true);
  };

  const handleUpdateApplication = async (values: any) => {
    try {
      await applicationAPI.updateApplication(appId!, values);
      message.success('Application updated successfully');
      setSettingsModal(false);
      await loadApplicationData();
    } catch (error) {
      console.error('Error updating application:', error);
      message.error('Failed to update application');
    }
  };

  // Logs handlers
  const loadLogs = async (resetPagination = false) => {
    if (!appId) return;
    if (loadingRef.current.logs) return;
    
    try {
      loadingRef.current.logs = true;
      setLogsLoading(true);
      // Transform filters to match backend expectations
      const params: any = {
        page: resetPagination ? 1 : pagination.current,
        pageSize: pagination.pageSize,
      };

      // Add search if provided
      if (logsFilter.search && logsFilter.search.trim()) {
        params.search = logsFilter.search.trim();
      }

      // Add status filter
      if (logsFilter.status && logsFilter.status.length > 0) {
        params.status = logsFilter.status;
      }

      // Add document_type filter
      if (logsFilter.document_type && logsFilter.document_type.length > 0) {
        params.document_type = logsFilter.document_type;
      }

      // Handle date range - backend expects date_from and date_to
      if (logsFilter.date_range && logsFilter.date_range.length === 2) {
        const [startDate, endDate] = logsFilter.date_range;
        if (startDate) params.date_from = startDate;
        if (endDate) params.date_to = endDate;
      }

      // Add other filters
      if (logsFilter.api_key_id) {
        params.api_key_id = logsFilter.api_key_id;
      }
      if (logsFilter.min_confidence) {
        params.confidence_min = logsFilter.min_confidence;
      }
      
      console.log('Loading logs with params:', params);
      const response = await applicationAPI.getLogs(appId, params);
      console.log('Logs API response:', response.data); // Debug log
      
      // Backend returns: { success: true, data: { data: [...], total, page, pageSize } }
      const responseData = response.data;
      if (responseData.success) {
        const { data, total, page, pageSize } = responseData.data;
        console.log('Extracted logs data:', { data, total, page, pageSize }); // Debug log
        
        setOcrRequests(Array.isArray(data) ? data : []);
        
        // Set flag BEFORE updating pagination to skip next useEffect trigger
        skipNextEffect.current = true;
        setPagination({
          current: page,
          pageSize,
          total
        });

        // Always extract and update document types from logs data to ensure we have the latest
        if (Array.isArray(data) && data.length > 0) {
          const uniqueDocTypes = Array.from(new Set(
            data
              .map((req: OCRRequest) => req.document_type)
              .filter((dt: string | undefined): dt is string => dt !== undefined && dt !== '')
          )).map((dt: string) => {
            // Create proper labels for common document types
            const labelMap: Record<string, string> = {
              'driverslicense': "Driver's License",
              'passport': 'Passport',
              'invoice': 'Invoice',
              'receipt': 'Receipt',
              'generaldocument': 'General Document',
              'aadhaar': 'Aadhaar',
              'aadhar': 'Aadhaar', // Alternative spelling
            };
            
            return {
              value: dt, // Keep the exact value from database
              label: labelMap[dt.toLowerCase()] || dt.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, str => str.toUpperCase())
            };
          });
          
          // Merge with existing document types to avoid losing any that might not be in current page
          setAvailableDocumentTypes(prevTypes => {
            const existingValues = new Set(prevTypes.map(t => t.value));
            const newTypes = uniqueDocTypes.filter(t => !existingValues.has(t.value));
            const merged = [...prevTypes, ...newTypes];
            console.log('Updated document types from logs:', merged);
            return merged;
          });
        }
      } else {
        console.error('API call failed:', responseData.message);
        message.error(responseData.message || 'Failed to load logs');
        setOcrRequests([]);
        setPagination(prev => ({ ...prev, total: 0 }));
      }
    } catch (error) {
      console.error('Error loading logs:', error);
      message.error('Failed to load request logs');
      setOcrRequests([]);
      setPagination(prev => ({ ...prev, total: 0 }));
    } finally {
      setLogsLoading(false);
      loadingRef.current.logs = false;
    }
  };

  const handleFilterChange = (newFilter: Partial<LogsFilter>) => {
    console.log('Filter changed:', newFilter);
    setLogsFilter(prev => {
      const updated = { ...prev, ...newFilter };
      console.log('Updated logsFilter:', updated);
      return updated;
    });
  };

  // Removed: duplicate useEffect that was causing double API calls
  // Filter changes are now handled in the main useEffect above

  const handleSearch = () => {
    console.log('Search triggered with filters:', logsFilter);
    loadLogs(true);
  };

  const handlePaginationChange = (page: number, pageSize?: number) => {
    setPagination(prev => ({
      ...prev,
      current: page,
      pageSize: pageSize || prev.pageSize
    }));
  };

  const handleViewRequestDetails = (request: OCRRequest) => {
    setSelectedRequest(request);
    setRequestDetailsModal(true);
  };

  const handleViewImage = (request: OCRRequest) => {
    setSelectedRequest(request);
    setImagePreviewModal(true);
  };

  const handleRegenerateImageUrl = async (request: OCRRequest) => {
    if (!appId) return;
    
    try {
      const response = await applicationAPI.regenerateApplicationLogImageUrl(appId, request.id, {
        expiration_hours: 24
      });
      
      if (response.data.success) {
        const newImageUrl = response.data.data.image_url;
        
        // Update the request in the list
        setOcrRequests(prevRequests => 
          prevRequests.map(req => 
            req.id === request.id 
              ? { ...req, image_url: newImageUrl }
              : req
          )
        );
        
        // Update selected request if it's the same one
        if (selectedRequest && selectedRequest.id === request.id) {
          setSelectedRequest({ ...selectedRequest, image_url: newImageUrl });
        }
        
        message.success('Image URL regenerated successfully');
      } else {
        message.error(response.data.message || 'Failed to regenerate image URL');
      }
    } catch (error) {
      console.error('Error regenerating image URL:', error);
      message.error('Failed to regenerate image URL');
    }
  };

  const handleExportLogs = async (format: 'csv' | 'json' | 'xlsx', selectedOnly = false) => {
    if (!appId) return;
    
    try {
      const params = {
        format,
        ...(selectedOnly && selectedLogIds.length > 0 ? { request_ids: selectedLogIds } : {}),
        ...(!selectedOnly ? { filters: logsFilter } : {})
      };
      
      const response = await applicationAPI.exportLogs(appId, params);
      
      // Create download link
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `ocr-logs-${application?.name}-${new Date().toISOString().split('T')[0]}.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      message.success(`Logs exported as ${format.toUpperCase()}`);
    } catch (error) {
      console.error('Error exporting logs:', error);
      message.error('Failed to export logs');
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    message.success('Copied to clipboard');
  };

  // Render logs tab content
  const renderLogsTab = () => {
    const logsColumns = [
      {
        title: '',
        key: 'select',
        width: 50,
        render: (_: any, record: OCRRequest) => (
          <Checkbox
            checked={selectedLogIds.includes(record.id)}
            onChange={(e) => {
              if (e.target.checked) {
                setSelectedLogIds(prev => [...prev, record.id]);
              } else {
                setSelectedLogIds(prev => prev.filter(id => id !== record.id));
              }
            }}
          />
        ),
      },
      {
        title: 'Request ID',
        dataIndex: 'id',
        key: 'id',
        width: 200,
        render: (id: string) => (
          <Text code copyable={{ text: id }}>
            {id.substring(0, 8)}...
          </Text>
        ),
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        width: 100,
        render: (status: string) => {
          const colors = {
            completed: 'green',
            processing: 'blue',
            failed: 'red'
          };
          return <Tag color={colors[status as keyof typeof colors]}>{status.toUpperCase()}</Tag>;
        },
      },
      {
        title: 'Document Type',
        dataIndex: 'document_type',
        key: 'document_type',
        width: 120,
        render: (type: string) => type || <Text type="secondary">General</Text>,
      },
      {
        title: 'Processing Time',
        dataIndex: 'processing_time_ms',
        key: 'processing_time_ms',
        width: 120,
        render: (time: number) => `${time}ms`,
      },
      {
        title: 'Confidence',
        dataIndex: 'confidence_score',
        key: 'confidence_score',
        width: 100,
        render: (score: number) => (
          <div>
            <Progress 
              percent={Math.round(score * 100)} 
              size="small" 
              status={score > 0.8 ? 'success' : score > 0.6 ? 'normal' : 'exception'}
            />
            <Text type="secondary">{Math.round(score * 100)}%</Text>
          </div>
        ),
      },
      {
        title: 'Created At',
        dataIndex: 'created_at',
        key: 'created_at',
        width: 150,
        render: (date: string) => new Date(date).toLocaleString(),
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 250,
        render: (_: any, record: OCRRequest) => (
          <Space>
            <Tooltip title="View Details">
              <Button
                size="small"
                icon={<EyeOutlined />}
                onClick={() => handleViewRequestDetails(record)}
              />
            </Tooltip>
            <Tooltip title="View Image">
              <Button
                size="small"
                icon={<FileImageOutlined />}
                onClick={() => handleViewImage(record)}
                disabled={!record.image_url}
              />
            </Tooltip>
            {record.image_url && (
              <Tooltip title="Regenerate Image URL">
                <Button
                  size="small"
                  icon={<ReloadOutlined />}
                  onClick={() => handleRegenerateImageUrl(record)}
                />
              </Tooltip>
            )}
            <Tooltip title="Download JSON">
              <Button
                size="small"
                icon={<DownloadOutlined />}
                onClick={() => handleExportLogs('json', false)}
              />
            </Tooltip>
          </Space>
        ),
      },
    ];

    return (
      <div>
        {/* Filters and Search */}
        <Card style={{ marginBottom: 16 }}>
          <Row gutter={[16, 16]}>
            <Col span={8}>
              <Input
                placeholder="Search by request ID, document type..."
                prefix={<SearchOutlined />}
                value={logsFilter.search}
                onChange={(e) => handleFilterChange({ search: e.target.value })}
                onPressEnter={handleSearch}
              />
            </Col>
            <Col span={4}>
              <Select
                placeholder="Status"
                mode="multiple"
                allowClear
                style={{ width: '100%' }}
                value={logsFilter.status}
                onChange={(value) => handleFilterChange({ status: value })}
              >
                <Option value="completed">Completed</Option>
                <Option value="processing">Processing</Option>
                <Option value="failed">Failed</Option>
              </Select>
            </Col>
            <Col span={4}>                <Select
                  placeholder="Document Type"
                  mode="multiple"
                  allowClear
                  style={{ width: '100%' }}
                  value={logsFilter.document_type}
                  onChange={(value) => {
                    console.log('Document type filter changed to:', value);
                    handleFilterChange({ document_type: value });
                  }}
                >
                  {availableDocumentTypes.map(type => (
                    <Option key={type.value} value={type.value}>
                      {type.label}
                    </Option>
                  ))}
                </Select>
            </Col>
            <Col span={6}>
              <RangePicker
                style={{ width: '100%' }}
                onChange={(dates, dateStrings) => 
                  handleFilterChange({ date_range: dateStrings as [string, string] })
                }
              />
            </Col>
            <Col span={2}>
              <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
                Search
              </Button>
            </Col>
          </Row>
          
          <Divider />
          
          <Row justify="space-between" align="middle">
            <Col>
              <Space>
                <Text strong>Total: {pagination.total} requests</Text>
                {selectedLogIds.length > 0 && (
                  <Text type="secondary">({selectedLogIds.length} selected)</Text>
                )}
              </Space>
            </Col>
            <Col>
              <Space>
                <Button
                  icon={<ExportOutlined />}
                  onClick={() => handleExportLogs('csv', selectedLogIds.length > 0)}
                >
                  Export CSV
                </Button>
                <Button
                  icon={<ExportOutlined />}
                  onClick={() => handleExportLogs('json', selectedLogIds.length > 0)}
                >
                  Export JSON
                </Button>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={() => loadLogs()}
                  loading={logsLoading}
                >
                  Refresh
                </Button>
              </Space>
            </Col>
          </Row>
        </Card>

        {/* Logs Table */}
        <Table
          columns={logsColumns}
          dataSource={Array.isArray(ocrRequests) ? ocrRequests : []}
          rowKey="id"
          loading={logsLoading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} requests`,
            onChange: handlePaginationChange,
          }}
          scroll={{ x: 1200 }}
        />
      </div>
    );
  };

  if (loading) {
    return (
      <LoadingAnimation
        type="rocket"
        message="Loading application details..."
        size="large"
      />
    );
  }

  if (!application) {
    return <div>Application not found</div>;
  }

  const apiKeyColumns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={status === 'active' ? 'green' : status === 'revoked' ? 'red' : 'orange'}>
          {status.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Total Requests',
      dataIndex: 'total_requests',
      key: 'total_requests',
      render: (count: number) => count?.toLocaleString() || 0,
    },
    {
      title: 'Last Used',
      dataIndex: 'last_used_at',
      key: 'last_used_at',
      render: (date: string) => date ? new Date(date).toLocaleDateString() : 'Never',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: APIKey) => (
        <Space>
          <Popconfirm
            title="Are you sure you want to revoke this API key?"
            onConfirm={() => handleRevokeApiKey(record.id)}
            disabled={record.status === 'revoked'}
          >
            <Button 
              size="small" 
              danger
              disabled={record.status === 'revoked'}
              icon={<DeleteOutlined />}
            >
              Revoke
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Layout>
      <Content style={{ padding: '24px' }}>
        <div style={{ marginBottom: '16px' }}>
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={() => navigate('/api-management')}
          >
            Back to Applications
          </Button>
        </div>

        <Row gutter={[24, 24]}>
          <Col span={24}>
            <Card>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <Title level={2}>{application.name}</Title>
                <Space>
                  <Tag color={application.status === 'active' ? 'green' : 'orange'}>
                    {application.status.toUpperCase()}
                  </Tag>
                  <Button icon={<SettingOutlined />} onClick={handleOpenSettings}>Settings</Button>
                </Space>
              </div>
              
              <Paragraph>{application.description}</Paragraph>
              
              <Row gutter={16}>
                <Col span={6}>
                  <Statistic title="Quota Used" value={application.total_requests} suffix={`/ ${application.quota_limit}`} />
                  <Progress 
                    percent={Math.round((application.total_requests / application.quota_limit) * 100)} 
                    status={application.total_requests > application.quota_limit * 0.8 ? 'exception' : 'normal'}
                  />
                </Col>
                <Col span={6}>
                  <Statistic title="Total Requests" value={application.total_requests} />
                </Col>
                <Col span={6}>
                  <Statistic title="Rate Limit" value={application.rate_limit_per_minute} suffix="req/min" />
                </Col>
                <Col span={6}>
                  <Statistic 
                    title="API Keys" 
                    value={application.api_keys?.filter(k => k.status === 'active').length || 0} 
                    suffix={`/ ${application.api_keys?.length || 0}`}
                  />
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>

        <Row gutter={[24, 24]} style={{ marginTop: '24px' }}>
          <Col span={24}>
            <Card>
              <Tabs activeKey={activeTab} onChange={setActiveTab}>
                <TabPane tab="Overview" key="overview">
                  <Descriptions bordered>
                    <Descriptions.Item label="Application ID">{application.id}</Descriptions.Item>
                    <Descriptions.Item label="Status">
                      <Tag color={application.status === 'active' ? 'green' : 'orange'}>
                        {application.status.toUpperCase()}
                      </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Created">{new Date(application.created_at).toLocaleDateString()}</Descriptions.Item>
                    <Descriptions.Item label="Quota Limit">{application.quota_limit.toLocaleString()}</Descriptions.Item>
                    <Descriptions.Item label="Quota Used">{application.total_requests.toLocaleString()}</Descriptions.Item>
                    <Descriptions.Item label="Rate Limit">{application.rate_limit_per_minute} requests/minute</Descriptions.Item>
                  </Descriptions>
                </TabPane>

                <TabPane 
                  tab={
                    <Badge count={application.api_keys?.length || 0} size="small">
                      <KeyOutlined /> API Keys
                    </Badge>
                  } 
                  key="api-keys"
                >
                  <div style={{ marginBottom: '16px' }}>
                    <Button 
                      type="primary" 
                      icon={<PlusOutlined />}
                      onClick={() => setCreateKeyModal(true)}
                    >
                      Create API Key
                    </Button>
                  </div>
                  
                  <Table
                    columns={apiKeyColumns}
                    dataSource={application.api_keys || []}
                    rowKey="id"
                    pagination={false}
                  />
                </TabPane>

                <TabPane tab={<><BarChartOutlined /> Analytics</>} key="analytics">
                  {application && (
                    <ApplicationAnalytics 
                      applicationId={application.id} 
                      applicationName={application.name}
                      showTitle={false}
                    />
                  )}
                </TabPane>

                <TabPane tab={<><HistoryOutlined /> Logs</>} key="logs">
                  {renderLogsTab()}
                </TabPane>

                <TabPane tab={<><CodeOutlined /> Integration Guide</>} key="integration">
                  {integrationGuide && (
                    <div>
                      <Alert
                        message="Ultra-Fast OCR API - Optimized for Speed"
                        description="This API delivers OCR results in under 3 seconds with Redis caching. All logging, analytics, and storage happen in background for instant response."
                        type="success"
                        showIcon
                        style={{ marginBottom: '24px' }}
                      />

                      <Row gutter={24}>
                        <Col span={16}>
                          <Title level={4}>🚀 Main OCR Endpoint</Title>
                          <Card style={{ marginBottom: '24px' }}>
                            <Row gutter={16}>
                              <Col span={12}>
                                <Text strong>Endpoint:</Text>
                                <br />
                                <Text code>{integrationGuide.api_endpoints.base_url}/v2/app-ocr/process</Text>
                                <Tooltip title="Copy endpoint URL">
                                  <Button 
                                    size="small" 
                                    icon={<CopyOutlined />} 
                                    onClick={() => copyToClipboard(`${integrationGuide.api_endpoints.base_url}/v2/app-ocr/process`)}
                                    style={{ marginLeft: '8px' }}
                                  />
                                </Tooltip>
                              </Col>
                              <Col span={12}>
                                <Text strong>Method:</Text> <Tag color="green">POST</Tag>
                                <br />
                                <Text strong>Response Time:</Text> <Tag color="orange">&lt; 3 seconds</Tag>
                              </Col>
                            </Row>
                            
                            <Divider />
                            
                            <Row gutter={16}>
                              <Col span={12}>
                                <Title level={5}>Required Parameters:</Title>
                                <ul>
                                  <li><Text code>image</Text> - Image file (JPEG, PNG, max 10MB)</li>
                                  <li><Text code>api_key</Text> - Your application API key</li>
                                </ul>
                              </Col>
                              <Col span={12}>
                                <Title level={5}>Optional Parameters:</Title>
                                <ul>
                                  <li><Text code>document_type</Text> - For structured extraction</li>
                                  <li><Text code>store_image</Text> - false (default, for speed)</li>
                                </ul>
                              </Col>
                            </Row>

                            {integrationGuide.api_endpoints.endpoints[0]?.features && (
                              <>
                                <Divider />
                                <Title level={5}>✨ Key Features:</Title>
                                <Row gutter={8}>
                                  {integrationGuide.api_endpoints.endpoints[0].features.map((feature, index) => (
                                    <Col span={12} key={index}>
                                      <Tag color="blue" style={{ margin: '2px' }}>{feature}</Tag>
                                    </Col>
                                  ))}
                                </Row>
                              </>
                            )}
                          </Card>

                          <Title level={4}>📝 Code Examples</Title>
                          <Tabs>
                            {integrationGuide.code_examples.curl && (
                              <TabPane tab="cURL" key="curl">
                                <pre style={{ backgroundColor: '#f5f5f5', padding: '16px', borderRadius: '4px', fontSize: '12px' }}>
                                  {integrationGuide.code_examples.curl.replace('YOUR_API_KEY', application.api_keys?.find(k => k.status === 'active')?.id || 'YOUR_API_KEY')}
                                </pre>
                                <Button 
                                  icon={<CopyOutlined />}
                                  onClick={() => copyToClipboard(integrationGuide.code_examples.curl?.replace('YOUR_API_KEY', application.api_keys?.find(k => k.status === 'active')?.id || 'YOUR_API_KEY') || '')}
                                >
                                  Copy cURL
                                </Button>
                              </TabPane>
                            )}
                            
                            {integrationGuide.code_examples.python && (
                              <TabPane tab="Python" key="python">
                                <pre style={{ backgroundColor: '#f5f5f5', padding: '16px', borderRadius: '4px', fontSize: '12px' }}>
                                  {integrationGuide.code_examples.python.replace('YOUR_API_KEY', application.api_keys?.find(k => k.status === 'active')?.id || 'YOUR_API_KEY')}
                                </pre>
                                <Button 
                                  icon={<CopyOutlined />}
                                  onClick={() => copyToClipboard(integrationGuide.code_examples.python?.replace('YOUR_API_KEY', application.api_keys?.find(k => k.status === 'active')?.id || 'YOUR_API_KEY') || '')}
                                >
                                  Copy Python
                                </Button>
                              </TabPane>
                            )}
                            
                            {integrationGuide.code_examples.javascript && (
                              <TabPane tab="JavaScript" key="javascript">
                                <pre style={{ backgroundColor: '#f5f5f5', padding: '16px', borderRadius: '4px', fontSize: '12px' }}>
                                  {integrationGuide.code_examples.javascript.replace('YOUR_API_KEY', application.api_keys?.find(k => k.status === 'active')?.id || 'YOUR_API_KEY')}
                                </pre>
                                <Button 
                                  icon={<CopyOutlined />}
                                  onClick={() => copyToClipboard(integrationGuide.code_examples.javascript?.replace('YOUR_API_KEY', application.api_keys?.find(k => k.status === 'active')?.id || 'YOUR_API_KEY') || '')}
                                >
                                  Copy JavaScript
                                </Button>
                              </TabPane>
                            )}

                            {integrationGuide.code_examples.node_js && (
                              <TabPane tab="Node.js" key="nodejs">
                                <pre style={{ backgroundColor: '#f5f5f5', padding: '16px', borderRadius: '4px', fontSize: '12px' }}>
                                  {integrationGuide.code_examples.node_js.replace('YOUR_API_KEY', application.api_keys?.find(k => k.status === 'active')?.id || 'YOUR_API_KEY')}
                                </pre>
                                <Button 
                                  icon={<CopyOutlined />}
                                  onClick={() => copyToClipboard(integrationGuide.code_examples.node_js?.replace('YOUR_API_KEY', application.api_keys?.find(k => k.status === 'active')?.id || 'YOUR_API_KEY') || '')}
                                >
                                  Copy Node.js
                                </Button>
                              </TabPane>
                            )}
                          </Tabs>
                        </Col>
                        
                        <Col span={8}>
                          <Title level={4}>📊 API Information</Title>
                          <Card style={{ marginBottom: '16px' }}>
                            <Statistic title="Active API Keys" value={integrationGuide.active_api_keys} />
                            <Statistic title="Rate Limit" value={`${integrationGuide.rate_limits.requests_per_minute}/min`} />
                            <Progress 
                              percent={(integrationGuide.rate_limits.quota_used / integrationGuide.rate_limits.quota_limit) * 100}
                              format={() => `${integrationGuide.rate_limits.quota_used}/${integrationGuide.rate_limits.quota_limit}`}
                              status={integrationGuide.rate_limits.quota_used / integrationGuide.rate_limits.quota_limit > 0.8 ? 'exception' : 'normal'}
                            />
                          </Card>

                          {integrationGuide.best_practices && (
                            <Card title="💡 Best Practices" size="small" style={{ marginBottom: '16px' }}>
                              <Tabs size="small">
                                {integrationGuide.best_practices?.performance && (
                                  <TabPane tab="Performance" key="performance">
                                    <ul style={{ fontSize: '12px' }}>
                                      {integrationGuide.best_practices.performance.map((tip, index) => (
                                        <li key={index}>{tip}</li>
                                      ))}
                                    </ul>
                                  </TabPane>
                                )}
                                {integrationGuide.best_practices?.integration && (
                                  <TabPane tab="Integration" key="integration_tips">
                                    <ul style={{ fontSize: '12px' }}>
                                      {integrationGuide.best_practices.integration.map((tip, index) => (
                                        <li key={index}>{tip}</li>
                                      ))}
                                    </ul>
                                  </TabPane>
                                )}
                              </Tabs>
                            </Card>
                          )}

                          {integrationGuide.api_endpoints.endpoints.length > 1 && (
                            <Card title="📋 Other Endpoints" size="small">
                              {integrationGuide.api_endpoints.endpoints.slice(1).map((endpoint, index) => (
                                <div key={index} style={{ marginBottom: '8px' }}>
                                  <Text strong>{endpoint.method}</Text> <Text code>{endpoint.path}</Text>
                                  <br />
                                  <Text type="secondary" style={{ fontSize: '11px' }}>{endpoint.description}</Text>
                                </div>
                              ))}
                            </Card>
                          )}
                        </Col>
                      </Row>

                      {integrationGuide.response_format && (
                        <>
                          <Divider />
                          <Title level={4}>📤 Response Format</Title>
                          <Row gutter={16}>
                            <Col span={12}>
                              <Card title="✅ Success Response" size="small">
                                <pre style={{ backgroundColor: '#f6ffed', padding: '12px', borderRadius: '4px', fontSize: '11px', overflow: 'auto' }}>
                                  {JSON.stringify(integrationGuide.response_format.success_response, null, 2)}
                                </pre>
                              </Card>
                            </Col>
                            <Col span={12}>
                              <Card title="❌ Error Response" size="small">
                                <pre style={{ backgroundColor: '#fff2f0', padding: '12px', borderRadius: '4px', fontSize: '11px', overflow: 'auto' }}>
                                  {JSON.stringify(integrationGuide.response_format.error_response, null, 2)}
                                </pre>
                              </Card>
                            </Col>
                          </Row>
                        </>
                      )}
                    </div>
                  )}
                </TabPane>
              </Tabs>
            </Card>
          </Col>
        </Row>

        {/* Create API Key Modal */}
        <Modal
          title="Create API Key"
          open={createKeyModal}
          onCancel={() => {
            setCreateKeyModal(false);
            setNewApiKey('');
            form.resetFields();
          }}
          footer={null}
        >
          {newApiKey ? (
            <div>
              <Alert
                message="API Key Created Successfully"
                description="This API key will only be shown once. Please save it securely."
                type="success"
                showIcon
                style={{ marginBottom: '16px' }}
              />
              <div style={{ backgroundColor: '#f5f5f5', padding: '16px', borderRadius: '4px', fontFamily: 'monospace' }}>
                {newApiKey}
                <Button 
                  size="small" 
                  icon={<CopyOutlined />}
                  onClick={() => copyToClipboard(newApiKey)}
                  style={{ float: 'right' }}
                >
                  Copy
                </Button>
              </div>
              <div style={{ marginTop: '16px', textAlign: 'right' }}>
                <Button onClick={() => {
                  setCreateKeyModal(false);
                  setNewApiKey('');
                }}>
                  Close
                </Button>
              </div>
            </div>
          ) : creatingApiKey ? (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <LoadingAnimation
                type="wave"
                message="Creating API key..."
                size="default"
              />
            </div>
          ) : (
            <Form form={form} onFinish={handleCreateApiKey}>
              <Form.Item
                name="name"
                label="Key Name"
                rules={[{ required: true, message: 'Please enter a name for the API key' }]}
              >
                <Input placeholder="e.g., Production Key" />
              </Form.Item>
              <Form.Item
                name="description"
                label="Description"
              >
                <TextArea rows={3} placeholder="Optional description for this API key" />
              </Form.Item>
              <div style={{ textAlign: 'right' }}>
                <Space>
                  <Button onClick={() => setCreateKeyModal(false)} disabled={creatingApiKey}>Cancel</Button>
                  <Button type="primary" htmlType="submit" loading={creatingApiKey}>
                    {creatingApiKey ? 'Creating...' : 'Create Key'}
                  </Button>
                </Space>
              </div>
            </Form>
          )}
        </Modal>

        {/* Settings Modal */}
        <Modal
          title="Application Settings"
          open={settingsModal}
          onCancel={() => setSettingsModal(false)}
          footer={null}
          width={600}
        >
          <Form
            form={settingsForm}
            layout="vertical"
            onFinish={handleUpdateApplication}
          >
            <Form.Item
              name="name"
              label="Application Name"
              rules={[{ required: true, message: 'Please enter application name' }]}
            >
              <Input placeholder="Enter application name" />
            </Form.Item>

            <Form.Item
              name="description"
              label="Description"
            >
              <TextArea rows={3} placeholder="Application description" />
            </Form.Item>

            <Form.Item
              name="quota_limit"
              label="Quota Limit"
              rules={[{ required: true, message: 'Please enter quota limit' }]}
            >
              <Input type="number" placeholder="Monthly request quota" />
            </Form.Item>

            <Form.Item
              name="rate_limit_per_minute"
              label="Rate Limit (requests per minute)"
              rules={[{ required: true, message: 'Please enter rate limit' }]}
            >
              <Input type="number" placeholder="Requests per minute" />
            </Form.Item>

            <div style={{ textAlign: 'right' }}>
              <Space>
                <Button onClick={() => setSettingsModal(false)}>Cancel</Button>
                <Button type="primary" htmlType="submit">Update Application</Button>
              </Space>
            </div>
          </Form>
        </Modal>

        {/* Request Details Modal */}
        <Modal
          title="OCR Request Details"
          open={requestDetailsModal}
          onCancel={() => setRequestDetailsModal(false)}
          width={800}
          footer={[
            <Button key="close" onClick={() => setRequestDetailsModal(false)}>
              Close
            </Button>,
            selectedRequest && (
              <Button
                key="export"
                type="primary"
                icon={<DownloadOutlined />}
                onClick={() => {
                  const dataStr = JSON.stringify(selectedRequest, null, 2);
                  const dataBlob = new Blob([dataStr], {type: 'application/json'});
                  const url = URL.createObjectURL(dataBlob);
                  const link = document.createElement('a');
                  link.href = url;
                  link.download = `ocr-request-${selectedRequest.id}.json`;
                  link.click();
                  URL.revokeObjectURL(url);
                }}
              >
                Download JSON
              </Button>
            )
          ]}
        >
          {selectedRequest && (
            <div>
              <Descriptions column={2} bordered>
                <Descriptions.Item label="Request ID">
                  <Text code copyable>{selectedRequest.id}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="Status">
                  <Tag color={selectedRequest.status === 'completed' ? 'green' : 
                             selectedRequest.status === 'processing' ? 'blue' : 'red'}>
                    {selectedRequest.status.toUpperCase()}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Document Type">
                  {selectedRequest.document_type || 'General'}
                </Descriptions.Item>
                <Descriptions.Item label="Schema Version">
                  {selectedRequest.schema_version || 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="Processing Time">
                  {selectedRequest.processing_time_ms}ms
                </Descriptions.Item>
                <Descriptions.Item label="Confidence Score">
                  <Progress 
                    percent={Math.round(selectedRequest.confidence_score * 100)} 
                    size="small"
                    status={selectedRequest.confidence_score > 0.8 ? 'success' : 
                           selectedRequest.confidence_score > 0.6 ? 'normal' : 'exception'}
                  />
                </Descriptions.Item>
                <Descriptions.Item label="File Size">
                  {selectedRequest.request_size_bytes} bytes
                </Descriptions.Item>
                <Descriptions.Item label="Created At">
                  {new Date(selectedRequest.created_at).toLocaleString()}
                </Descriptions.Item>
              </Descriptions>

              <Divider />

              <Tabs defaultActiveKey="extracted">
                <TabPane tab="Extracted Data" key="extracted">
                  <Card>
                    <pre style={{ 
                      backgroundColor: '#f5f5f5', 
                      padding: '16px', 
                      borderRadius: '4px',
                      maxHeight: '300px',
                      overflow: 'auto',
                      fontFamily: 'monospace'
                    }}>
                      {JSON.stringify(selectedRequest.extracted_data, null, 2)}
                    </pre>
                  </Card>
                </TabPane>

                <TabPane tab="Raw Text" key="raw">
                  <Card>
                    <TextArea
                      value={selectedRequest.raw_text || 'No raw text available'}
                      rows={10}
                      readOnly
                    />
                  </Card>
                </TabPane>

                {selectedRequest.error_message && (
                  <TabPane tab="Error Details" key="error">
                    <Alert
                      message="Processing Error"
                      description={selectedRequest.error_message}
                      type="error"
                      showIcon
                    />
                  </TabPane>
                )}

                <TabPane tab="Timeline" key="timeline">
                  <Timeline>
                    <Timeline.Item color="blue">
                      <p><strong>Request Submitted</strong></p>
                      <p>{new Date(selectedRequest.created_at).toLocaleString()}</p>
                    </Timeline.Item>
                    <Timeline.Item color={selectedRequest.status === 'completed' ? 'green' : 
                                          selectedRequest.status === 'failed' ? 'red' : 'blue'}>
                      <p><strong>{selectedRequest.status === 'completed' ? 'Processing Completed' :
                                 selectedRequest.status === 'failed' ? 'Processing Failed' :
                                 'Processing...'}</strong></p>
                      <p>{new Date(selectedRequest.updated_at).toLocaleString()}</p>
                      {selectedRequest.processing_time_ms && (
                        <p>Processing took {selectedRequest.processing_time_ms}ms</p>
                      )}
                    </Timeline.Item>
                  </Timeline>
                </TabPane>
              </Tabs>
            </div>
          )}
        </Modal>

        {/* Image Preview Modal */}
        <Modal
          title="Document Image Preview"
          open={imagePreviewModal}
          onCancel={() => setImagePreviewModal(false)}
          width={900}
          footer={[
            selectedRequest?.image_url && (
              <Button 
                key="regenerate"
                icon={<ReloadOutlined />}
                onClick={() => {
                  if (selectedRequest) {
                    handleRegenerateImageUrl(selectedRequest);
                  }
                }}
              >
                Regenerate URL
              </Button>
            ),
            <Button key="close" onClick={() => setImagePreviewModal(false)}>
              Close
            </Button>
          ]}
        >
          {selectedRequest && selectedRequest.image_url && (
            <div style={{ textAlign: 'center' }}>
              <Image
                src={selectedRequest.image_url}
                alt="OCR Document"
                style={{ maxWidth: '100%', maxHeight: '70vh' }}
                preview={{
                  toolbarRender: () => (
                    <Space>
                      <Button 
                        icon={<DownloadOutlined />}
                        onClick={() => {
                          const link = document.createElement('a');
                          link.href = selectedRequest.image_url!;
                          link.download = `ocr-document-${selectedRequest.id}`;
                          link.click();
                        }}
                      >
                        Download
                      </Button>
                    </Space>
                  )
                }}
              />
              <div style={{ marginTop: 16 }}>
                <Descriptions size="small" column={2}>
                  <Descriptions.Item label="Document Type">
                    {selectedRequest.document_type || 'General'}
                  </Descriptions.Item>
                  <Descriptions.Item label="File Size">
                    {selectedRequest.request_size_bytes} bytes
                  </Descriptions.Item>
                  <Descriptions.Item label="Processing Time">
                    {selectedRequest.processing_time_ms}ms
                  </Descriptions.Item>
                  <Descriptions.Item label="Confidence">
                    {Math.round(selectedRequest.confidence_score * 100)}%
                  </Descriptions.Item>
                </Descriptions>
              </div>
            </div>
          )}
          {selectedRequest && !selectedRequest.image_url && (
            <Empty description="No image available for this request" />
          )}
        </Modal>
      </Content>
    </Layout>
  );
};

export default ApplicationDetail;
